import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Axios instance with interceptors
const api = axios.create({
  baseURL: API_URL,
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors on protected endpoints (token expired / invalid).
// IMPORTANT: skip the auth endpoints themselves — a 401 from /api/auth/login
// means "wrong password", NOT "session expired", and hijacking that with a
// full page reload swallows the specific server error ("Account locked…",
// "Invalid email or password") and shows a generic "Invalid credentials"
// fallback toast instead.
const AUTH_ENDPOINT_MATCHERS = [
  '/api/auth/login',
  '/api/auth/login/2fa',
  '/api/auth/signup',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = AUTH_ENDPOINT_MATCHERS.some((path) => requestUrl.includes(path));
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Real session expiration on a protected endpoint — clear + redirect.
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      setCurrentUser(response.data);
      localStorage.setItem('currentUser', JSON.stringify(response.data));
    } catch (error) {
      // Token invalid, clear auth
      logout();
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in (has token)
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    
    if (token && storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      // Optionally verify token is still valid
      verifyToken();
    }
    setLoading(false);
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const data = response.data;

      // 2FA required for admin — caller must complete via loginVerify2FA
      if (data.requires_2fa) {
        return {
          success: false,
          requires2fa: true,
          pendingToken: data.pending_token,
          error: data.message || 'Enter 6-digit code from your authenticator app',
        };
      }

      const { token, user } = data;
      localStorage.setItem('token', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      setCurrentUser(user);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        // Fallback only fires if the request never reached the backend
        // (network / CORS / server down). In that case "Invalid credentials"
        // is misleading — the credentials were never even checked.
        error: error.response?.data?.detail
          || 'Login request could not reach the server. Please check your connection and try again.',
      };
    }
  };

  const loginVerify2FA = async (pendingToken, code) => {
    try {
      const response = await api.post('/api/auth/login/2fa', {
        pending_token: pendingToken,
        code,
      });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      setCurrentUser(user);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid 2FA code',
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/api/auth/signup', {
        email: userData.email,
        password: userData.password,
        name: userData.name,
        phone: userData.phone || null,
        role: 'customer'
      });
      
      const { token, user } = response.data;
      
      // Store token and user
      localStorage.setItem('token', token);
      localStorage.setItem('currentUser', JSON.stringify(user));
      setCurrentUser(user);
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
  };

  return (
    <AuthContext.Provider value={{ currentUser, login, loginVerify2FA, signup, logout, loading, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Export api instance for use in other components
export { api };